/* eslint-disable no-console */
import { PublicClientApplication } from '@azure/msal-browser';
import type { Configuration, AuthenticationResult, BrowserSystemOptions } from '@azure/msal-browser';
import { TENANT_ID } from 'utils/env';

/**
 * Construct a configuration object for MSAL
 * See https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications
 * @param azureAppId The id of the Azure Application
 * @param redirectUri The URI to redirect to when authenticated
 * @returns MSAL configuration object
 */
export const createMsalConfig = (azureAppId: string, redirectUri: string, systemOptions?: BrowserSystemOptions): Configuration => ({
    auth: {
        clientId: azureAppId,
        authority: `https://login.microsoftonline.com/${TENANT_ID}/`,
        redirectUri,
    },
    cache: {
        cacheLocation: 'sessionStorage', // This configures where your cache will be stored
    },
    system: systemOptions
});

/**
 * Log in and get your credentials, such as ID token, access token, and refresh token
 * visit https://jwt.ms/ to verify and inspect your token
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-acquire-token?tabs=javascript2
 * https://github.com/AzureAD/microsoft-authentication-library-for-js
 * https://azuread.github.io/microsoft-authentication-library-for-js/ref/classes/_azure_msal_browser.publicclientapplication.html
 * https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-acquire-cache-tokens
 * @param msalConfig The MSAL configuration object
 * @param scopes The scopes for which the token is to be issued
 * @returns MSAL configuration object
*/
export const AuthClient = (msalConfig: Configuration, tokenScopes?: Array<string>): AuthenticationClient => {
    const config = msalConfig;
    let pcaInstance: PublicClientApplication | null = null;

    const getPca = async (): Promise<PublicClientApplication> => {
        if (!pcaInstance) {
            pcaInstance = new PublicClientApplication(config);
            await pcaInstance.initialize();
        }

        return pcaInstance;
    };

    return {
        login: async (): Promise<AuthenticationResult | null | void> => {
            const pca = await getPca();

            return pca.handleRedirectPromise().then(async response => {
                if (!response) {
                    if (pca.getAllAccounts().length === 0) {
                        const loginRequest = {
                            scopes: ['User.Read', 'openid', 'profile', 'Group.Read.All'],
                        };
                        const redirect = await pca.loginRedirect(loginRequest);
                        console.info(redirect);
                    } else {
                        const loginRequest = {
                            scopes: tokenScopes || ['User.Read', 'openid', 'profile', 'Group.Read.All', 'offline_access'],
                            account: pca.getAllAccounts()[0],
                        };

                        return pca.acquireTokenSilent(loginRequest)
                            .catch(error => {
                                console.error(error);

                                return pca.loginRedirect(loginRequest);
                            });
                    }
                }

                return response;
            });
        },
        /**
         * Get the token for requesting API resources. This is different from the msftAccessToken, i.e. "accessToken"
         * @param msalConfig The MSAL configuration object
         * @returns
         */
        getAppAccessToken: async (): Promise<string> => {
            const pca = await getPca();

            const loginRequest = {
                scopes: ['openid', 'profile', `api://${config.auth.clientId}/api_access`],
                account: pca.getAllAccounts()[0],
            };

            return pca.acquireTokenSilent(loginRequest).then(auth => auth.accessToken);
        },
        /**
         * Get the MSFT access token, used for accessing AD and Graph API resources
         * @returns
         */
        getMsftAccessToken: async (): Promise<string> => {
            const pca = await getPca();

            const loginRequest = {
                scopes: ['profile'],
                account: pca.getAllAccounts()[0],
            };

            return pca
                .acquireTokenSilent(loginRequest)
                .then(auth => auth.accessToken);
        },
        /**
         * logout the current user
         */
        logout: async (): Promise<AuthenticationResult | null | void> => {
            const pca = await getPca();

            return pca.logoutRedirect()
                .catch(error => console.error(error));
        }
    };
};

type AuthenticationClient = {
    getAppAccessToken: () => Promise<string>,
    getMsftAccessToken: () => Promise<string>,
    login: () => Promise<AuthenticationResult | null | void>
    logout: () => Promise<AuthenticationResult | null | void>
};
